/**
 * Организации, запрещённые в РФ, в интерфейсе (build spec 2026-11-02 §3): предпросмотр редактора
 * и гейт утверждения. Флаг бренда — `banned_orgs` из `GET /api/v1/brands` (`brandBannedOrgs`).
 *
 * ЗВЁЗДОЧКИ И СНОСКУ СТАВИТ СЕРВЕР. Бэкенд прогоняет каждую сохраняемую версию бренда с
 * включённой разметкой через `applyBannedOrgMarks` (`prepare` приёма — до заморозки), и тело
 * сохранения редактора от этого не меняется: уходит текст, как его набрал человек. Интерфейс
 * только ПОКАЗЫВАЕТ, что сделает сохранение, — той же функцией контрактов, а не копией её правил:
 * какие названия считаются упоминанием, решает разметка, и список её форм может меняться
 * (решение Ильи 2026-11-03 о написаниях Facebook и Instagram). Здесь нет ни одного правила
 * сопоставления — только сравнение того, что функции контрактов вернули, с тем, что набрано.
 *
 * ЗАЧЕМ ПОКАЗЫВАТЬ ДО СОХРАНЕНИЯ. Сноска — ещё восемнадцать кодпоинтов в конце поста: правка у
 * порога 4064 без неё уходила бы обычным сообщением с карточкой, а с ней — расширенным и без
 * карточки. Счётчик, транспорт и превью обязаны считать то, что сохранится. И звёздочка, которую
 * человек стёр, вернётся: без строки об этом «Новой версии не появилось» читалось бы как сбой.
 *
 * ГЕЙТ — ТО ЖЕ ПРАВИЛО, ЧТО У БЭКЕНДА, ПОКАЗАННОЕ ЗАРАНЕЕ. Версии, сохранённые до разметки,
 * одобрение бэкенда не пропускает (`banned_org_unmarked`, страховка `checkedContent`), и
 * узнавать об этом в конце удержания «Утвердить» — поздно. Поэтому та же проверка
 * (`bannedOrgIssues` контрактов) — блокирующая `UiIssue` экрана черновика и пакетного экрана, как
 * гейт визуала (`visualIssues`): второй способ выключать кнопку утверждения означал бы два
 * правила, которые однажды ответят по-разному.
 */

import type { DraftState } from '@smm/contracts';
import {
  BANNED_ORG_FOOTNOTE,
  type BannedOrgContent,
  bannedOrgIssues,
  findBannedOrgMentions,
} from '@smm/contracts/preview';
import type { UiIssue } from './contracts.ts';

/** Что разметка сервера поменяет в набранном тексте. */
export type MarksChange =
  /** Поставит звёздочки после этих названий (и сноску, если её нет). */
  | { readonly kind: 'mark'; readonly forms: readonly string[] }
  /** Все названия помечены, а сноски в конце нет — вернёт её. */
  | { readonly kind: 'footnote_back' }
  /** Названий не осталось, а сноска стоит — уберёт её. */
  | { readonly kind: 'footnote_gone' }
  /** Поправит оформление пометки: курсив сноски, её место, пробелы перед ней. */
  | { readonly kind: 'format' };

/** Необязательные поля сущности — у `text_link` адрес, у `pre` язык. */
const urlOf = (e: object): unknown => (e as { readonly url?: unknown }).url;
const languageOf = (e: object): unknown => (e as { readonly language?: unknown }).language;

/** Совпадают ли два содержания: текст и каждая сущность — по каждому её полю, в том же порядке. */
export function sameContent(a: BannedOrgContent, b: BannedOrgContent): boolean {
  if (a.text !== b.text || a.entities.length !== b.entities.length) return false;
  return a.entities.every((e, i) => {
    const f = b.entities[i];
    return (
      f !== undefined &&
      e.type === f.type &&
      e.offset === f.offset &&
      e.length === f.length &&
      urlOf(e) === urlOf(f) &&
      languageOf(e) === languageOf(f)
    );
  });
}

const unique = (xs: readonly string[]): string[] => [...new Set(xs)];

/** Названия без звёздочки — формами, как они стоят в тексте, без повторов. */
const unmarkedForms = (content: BannedOrgContent): string[] =>
  unique(
    findBannedOrgMentions(content)
      .filter((m) => !m.marked)
      .map((m) => m.form),
  );

/**
 * Что поменяет сохранение: `typed` — набранное, `marked` — оно же после `applyBannedOrgMarks`.
 * `null` — разметка не меняет ничего (у бренда она выключена, названий нет, всё уже помечено).
 */
export function marksChangeOf(
  typed: BannedOrgContent,
  marked: BannedOrgContent,
): MarksChange | null {
  if (sameContent(typed, marked)) return null;
  const forms = unmarkedForms(typed);
  if (forms.length > 0) return { kind: 'mark', forms };
  if (findBannedOrgMentions(marked).length === 0) return { kind: 'footnote_gone' };
  if (bannedOrgIssues(typed).some((i) => i.kind === 'footnote_missing')) {
    return { kind: 'footnote_back' };
  }
  return { kind: 'format' };
}

/** Строка под полем редактора, пока правка не сохранена: что сделает сохранение. */
export function marksBeforeSave(change: MarksChange): string {
  switch (change.kind) {
    case 'mark':
      return `Сохранение пометит организации, запрещённые в РФ: ${change.forms.join(', ')} — звёздочкой после названия и сноской «${BANNED_ORG_FOOTNOTE}» последней строкой.`;
    case 'footnote_back':
      return `Сохранение вернёт сноску «${BANNED_ORG_FOOTNOTE}» последней строкой: в тексте есть организации, запрещённые в РФ.`;
    case 'footnote_gone':
      return `Сохранение уберёт сноску «${BANNED_ORG_FOOTNOTE}»: организаций, запрещённых в РФ, в тексте больше нет.`;
    case 'format':
      return 'Сохранение оформит пометку организаций, запрещённых в РФ, по правилу: звёздочка после названия, сноска курсивом последней строкой.';
  }
}

/** Добавка к подтверждению сохранения, родившего версию: что сделал сервер. */
export function marksAfterSave(change: MarksChange): string {
  switch (change.kind) {
    case 'mark':
      return `Сервер пометил организации, запрещённые в РФ: ${change.forms.join(', ')}.`;
    case 'footnote_back':
      return `Сервер вернул сноску «${BANNED_ORG_FOOTNOTE}».`;
    case 'footnote_gone':
      return `Сервер убрал сноску «${BANNED_ORG_FOOTNOTE}»: организаций, запрещённых в РФ, в тексте больше нет.`;
    case 'format':
      return 'Сервер оформил пометку организаций, запрещённых в РФ, по правилу.';
  }
}

/**
 * Подтверждение сохранения, НЕ родившего версию, когда причина — разметка: человек стёр звёздочку
 * или сноску, сервер их вернул, и содержимое совпало с прежней версией. «Совпало после
 * канонизации» назвало бы не ту причину, и человек правил бы ещё раз.
 */
export function marksNoVersion(change: MarksChange, versionNo: number): string {
  const v = `v${String(versionNo)}`;
  return change.kind === 'footnote_gone'
    ? `Новой версии не появилось: сервер убрал сноску «${BANNED_ORG_FOOTNOTE}» — организаций, запрещённых в РФ, в тексте нет, — и содержимое совпало с ${v}.`
    : `Новой версии не появилось: сервер вернул пометку организаций, запрещённых в РФ, и содержимое совпало с ${v}. Звёздочки и сноску ставит сервер — правкой их не убрать.`;
}

/** Чего не хватает сохранённой версии — словами; `null` — пометка в порядке. */
function missingMarks(saved: BannedOrgContent): string | null {
  const issues = bannedOrgIssues(saved);
  if (issues.length === 0) return null;
  const forms = unique(issues.flatMap((i) => (i.kind === 'unmarked' ? [i.form] : [])));
  return forms.length > 0
    ? `организации, запрещённые в РФ, без пометки: ${forms.join(', ')}`
    : `нет сноски «${BANNED_ORG_FOOTNOTE}» к пометкам`;
}

/**
 * Сохранённая версия без пометки (версия до разметки) — строка редактора рядом с кнопкой
 * «Сохранить с пометками». `null` — пометка в порядке.
 */
export function unmarkedVersionText(saved: BannedOrgContent): string | null {
  const what = missingMarks(saved);
  return what === null
    ? null
    : `В тексте версии ${what}. Утвердить её нельзя, пока она не сохранена заново: сохранение поставит звёздочки и сноску, текст останется вашим.`;
}

/**
 * Состояния с кнопкой утверждения, которую версию заново сохранить можно: ожидает и одобрена
 * без слота. Гейт стоит только там, где человек может его снять сам; у остальных состояний
 * утверждения нет, а несостоявшуюся публикацию бэкенд отобьёт своим текстом.
 */
const GATED_STATUSES: ReadonlySet<DraftState> = new Set(['pending_review', 'approved']);

/**
 * Гейт утверждения: версия бренда с разметкой, в которой есть неразмеченное название или нет
 * сноски, — блокирующая проблема с кодом отказа бэкенда (`banned_org_unmarked`). Бренд без
 * разметки (`on: false`) — гейта нет: сервер такие версии и не проверяет.
 */
export function bannedOrgGate(
  model: { readonly status: DraftState; readonly canonical: BannedOrgContent },
  on: boolean,
): readonly UiIssue[] {
  if (!on || !GATED_STATUSES.has(model.status)) return [];
  const what = missingMarks(model.canonical);
  if (what === null) return [];
  return [
    {
      level: 'blocking',
      code: 'banned_org_unmarked',
      message: `В тексте ${what}. Одобрение такую версию не пропустит: откройте пост, «Редактировать» → «Сохранить с пометками» — сервер поставит звёздочки и сноску, текст останется вашим.`,
    },
  ];
}
